/**
 * Type guards for Filevine field value types.
 *
 * Consolidates type guard logic that was previously duplicated in:
 * - shared/FilevineEntityDetails.tsx
 * - utils/fieldRenderers.tsx
 */

import type {
  AttachedDocument,
  ComputedFieldValue,
  CoverageEntitySchema,
  MedicalProviderEntitySchema,
  ProjectClientSchema,
} from '@/services/api/generated/entitySyncService';

/**
 * Type guard for Person (ProjectClientSchema).
 *
 * Detects person objects by the presence of the 'fullname' property.
 *
 * @param value - Unknown value to check
 * @returns True if value is a ProjectClientSchema
 */
export function isPersonValue(value: unknown): value is ProjectClientSchema {
  return (
    typeof value === 'object' &&
    value !== null &&
    'fullname' in value &&
    typeof (value as ProjectClientSchema).fullname === 'string'
  );
}

/**
 * Type guard for Computed field (ComputedFieldValue).
 *
 * Detects computed fields by presence of 'value' or 'error' properties.
 * Excludes Person values which also have a 'value'-like structure.
 *
 * @param value - Unknown value to check
 * @returns True if value is a ComputedFieldValue
 */
export function isComputedValue(value: unknown): value is ComputedFieldValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('value' in value || 'error' in value) &&
    !('fullname' in value) // Exclude Person values
  );
}

/**
 * Type guard for Attachments (array of AttachedDocument).
 *
 * Detects attachment arrays by the presence of 'filename' property on array elements.
 *
 * @param value - Unknown value to check
 * @returns True if value is an array of AttachedDocument
 */
export function isAttachmentArray(value: unknown): value is AttachedDocument[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    typeof value[0] === 'object' &&
    value[0] !== null &&
    'filename' in value[0]
  );
}

export function isMedicalProviderEntity(
  value: unknown,
): value is MedicalProviderEntitySchema {
  return (
    typeof value === 'object' &&
    value !== null &&
    'name' in value &&
    'status' in value &&
    'treatment_start_date' in value &&
    typeof (value as MedicalProviderEntitySchema).name === 'string'
  );
}

export function isCoverageEntity(
  value: unknown,
): value is CoverageEntitySchema {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    // Coverage entities have these distinctive fields that medical providers don't
    ('carrier_name' in value ||
      'policy_type' in value ||
      'category' in value ||
      'liability_status' in value) &&
    !('treatment_start_date' in value)
  );
}
